import { useRef } from 'react'
import { Link } from 'react-router'
import { CheckIcon } from '@heroicons/react/24/outline'
import {
  useClubAdminGetClubMembersQuery,
  useClubAdminListAddressesQuery,
  useClubAdminListScheduleQuery,
} from '../../features/SpeechAgendaApiSlice.ts'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'

// Tab indices in ClubAdminPanelPage: Schedule first, Members second.
const SCHEDULE_TAB = 0
const MEMBERS_TAB = 1

interface OnboardingChecklistProps {
  clubId: string
  onSelectTab: (index: number) => void
}

interface ChecklistStep {
  label: string
  hint: string
  done: boolean
  tabIndex: number
}

const OnboardingChecklist = ({
  clubId,
  onSelectTab,
}: OnboardingChecklistProps) => {
  const { userLoaded } = useAppAuth()
  const skip = !clubId || !userLoaded

  // Reuse the same hooks the child sections use. RTK Query dedupes by
  // endpoint+arg, so these don't fire extra requests.
  const { data: addresses, isSuccess: addrReady } =
    useClubAdminListAddressesQuery(clubId, { skip })
  const { data: meetings, isSuccess: meetReady } =
    useClubAdminListScheduleQuery(clubId, { skip })
  const { data: members, isSuccess: memReady } =
    useClubAdminGetClubMembersQuery(clubId, { skip })
  const dataReady = addrReady && meetReady && memReady

  const steps: ChecklistStep[] = [
    {
      label: 'Add a meeting location',
      hint: 'Every meeting needs an address.',
      done: (addresses?.length ?? 0) > 0,
      tabIndex: SCHEDULE_TAB,
    },
    {
      label: 'Schedule a meeting',
      hint: 'Set a recurring time to publish your club page.',
      done: (meetings?.length ?? 0) > 0,
      tabIndex: SCHEDULE_TAB,
    },
    {
      label: 'Add members',
      // The admin is already a member, so "added members" means more than one.
      hint: 'Invite people individually or import your roster.',
      done: (members?.length ?? 0) > 1,
      tabIndex: MEMBERS_TAB,
    },
  ]

  const allDone = steps.every((step) => step.done)
  const completedCount = steps.filter((step) => step.done).length

  // Latch the club's completeness once, when the initial data settles. Mutating
  // the ref during render is intentional: a one-time, idempotent latch (guarded
  // on `null`), so StrictMode's double render leaves the baseline untouched.
  const baselineComplete = useRef<boolean | null>(null)
  if (dataReady && baselineComplete.current === null) {
    baselineComplete.current = allDone
  }

  // Don't flash before data settles.
  if (!dataReady) return null
  // Already set up on load → the celebration is a one-time, in-session moment,
  // so a club that arrives complete stays silent.
  if (baselineComplete.current) return null

  // Started incomplete this session: show the celebration only on the live
  // transition to complete.
  if (allDone) {
    return (
      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-green-200 bg-green-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-medium text-green-800">
          Club is set up 🎉
        </p>
        <Link
          to={`/club/${clubId}`}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
          View your club page
        </Link>
      </div>
    )
  }

  return (
    <div className="mb-6 rounded-lg border border-indigo-100 bg-indigo-50 p-4">
      <header className="mb-3">
        <h2 className="text-sm font-semibold text-indigo-900">
          Finish setting up your club
        </h2>
        <p className="text-xs text-indigo-700">
          {completedCount} of {steps.length} steps complete
        </p>
      </header>

      <ol className="space-y-2">
        {steps.map((step, index) => (
          <li key={step.label}>
            <button
              type="button"
              onClick={() => onSelectTab(step.tabIndex)}
              className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-indigo-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
              <span
                className={[
                  'flex h-6 w-6 flex-none items-center justify-center rounded-full border text-xs font-semibold',
                  step.done
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-indigo-300 bg-white text-indigo-700',
                ].join(' ')}
                aria-hidden="true">
                {step.done ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </span>
              <span className="min-w-0">
                <span
                  className={[
                    'block text-sm font-medium',
                    step.done
                      ? 'text-gray-500 line-through'
                      : 'text-gray-900',
                  ].join(' ')}>
                  {step.label}
                </span>
                {!step.done && (
                  <span className="block text-xs text-gray-600">
                    {step.hint}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

export default OnboardingChecklist
